<i18n>
{
	"en": {
		"customer": "Customer",
		"customAddress": "Custom Address",
		"edit": "Edit",
		"failedToFetch": "Failed to search for addresses"
	},
	"no": {
		"customer": "Kunde",
		"customAddress": "Ny adresse",
		"edit": "Rediger",
		"failedToFetch": "Kunne ikke søke etter adresser"
	}
}
</i18n>

<template>
	<div>
		<t-input-card
			:header="$t('customer')"
			:title="valueData.street"
			:text="`${valueData.postalCode || ''} ${valueData.postalPlace || ''}`"
			:hideContent="!value || !valueData.name"
			:hidePadding="hidePadding"
		>
			<template #input>

				<t-combobox-button
					ref="comboboxButton"
					icon="geosearch"
					:limit="$options.SERVER_RETURN_LIMIT"

					:searchValue="searchValue"
					:items="filteredItems"
					:selected="value"

					:disabled="disabled"
					:disableButtonTabbing="disableButtonTabbing"

					@focus="onFocus"
					@input="onInput"
					@change="onChange"
					@blur="onBlur"
					@button:click="setShowDialog(true)"
				>
					<template
						#optionItemContent="{ item }"
					>
						<div class="item">
							<div class="item-main-text">{{ item.name }}</div>
							<div class="item-sub-text">{{ item.customerNo }}</div>
						</div>
					</template>
				</t-combobox-button>
			</template>

			<template #title-after>
				<div>
					{{ valueData.customerNo }}
				</div>
			</template>
		</t-input-card>

		<CustomersInputDialog
			:items="customers"
			:isShown="showDialog"
			:initialSearchValue="searchValue"
			icon="geosearch"
			@close="setShowDialog(false)"
			@ok="$emit('change', $event)"
		/>
	</div>
</template>

<script>
import { mapActions, mapState } from './store/customers.store.js';
import CustomersInputDialog from './CustomersInput/CustomersDialog';
import TInputCard from '@/components/evergreen/timpex/inputs/TInputCard';
import TComboboxButton from '@/components/evergreen/timpex/inputs/TComboboxButton';

export default {
	name: 'CustomerInput',
	SERVER_RETURN_LIMIT: 50,
	components: { TComboboxButton, TInputCard, CustomersInputDialog },
	props: {
		value: Object,
		title: String,
		disabled: {
			type: Boolean,
			default: false
		},
		disableButtonTabbing: {
			type: Boolean,
			default: false
		},
		hidePadding: Boolean,
	},
	data() {
		return {
			showDialog: false,
			searchValue: '',
			searchTimeout: null,
			focused: false,
			lastSearch: null,
		};
	},
	computed: {
		...mapState(['customers']),

		filteredItems() {
			if (this.searchValue === '')
				return this.customers;

			return this.customers.filter( item => {

				if (!item)
					return false;

				let loweredSearchValue = this.searchValue.toLowerCase();

				let ret = true;
				for (let c of loweredSearchValue.split(' ')) {
					ret = ret &&
						( (item.name && item.name.toLowerCase().includes(c))
							|| ( item.customerNo && item.customerNo.toString().toLowerCase().includes(c) )
						);
				}
				return ret;
			});

		},
		valueData() {
			return this.value ? this.value : {};
		}
	},
	watch: {
		value: {
			immediate: true,

			handler(value) {
				this.searchValue = this.getSearchValueFromValue(value);
			}
		}
	},
	methods: {
		...mapActions([ 'getAllCustomers', 'getCustomers' ]),

		onFocus() {
			this.focused = true;
		},
		onInput(searchValue) {
			this.searchValue = searchValue;

			if (this.shouldCancelNewSearch(searchValue, this.lastSearch, this.customers))
				return;

			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {

				this.search(searchValue);

			}, 250);
		},
		shouldCancelNewSearch(searchValue, lastSearch, items) {
			return (
				searchValue === ''
				|| (
					items.length < this.$options.SERVER_RETURN_LIMIT
					&& searchValue.startsWith( lastSearch )
				)
			);
		},
		onChange(event) {

			this.searchValue = this.getSearchValueFromValue(event);

			this.$emit('change', event);
		},
		onBlur(event) {

			this.focused = false;

			if (!isRelatedTargetButton(event, this.$refs.comboboxButton)) {

				if (this.searchValue === '')
					this.$emit('change', {});

				else
					this.searchValue = this.getSearchValueFromValue(this.value);
			}

			function isRelatedTargetButton(event, comboboxButtonRef) {

				return event.relatedTarget
					=== comboboxButtonRef.$refs.textInputButton.$refs.iconButton.$el;
			}
		},
		async search(searchValue) {
			try {
				// getting all cusomers takes almost a minute on veøy møre
				// await this.getAllCustomers();
				await this.getCustomers(searchValue);

				if (
					!this.focused
					&& this.customers
					&& this.customers.length === 1
				) {
					this.$emit('change', this.customers[0]);
					this.searchValue = this.getSearchValueFromValue(this.customers[0]);
				}
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
			this.lastSearch = searchValue;
		},
		getSearchValueFromValue(value) {

			if (value && value.name && value.name.length)
				return value.name;
			else
				return '';
		},
		setShowDialog(showDialog) {

			if (this.showDialog === true && showDialog === false)
				this.searchValue = this.getSearchValueFromValue(this.value);

			this.showDialog = showDialog;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.item {
	height: 100%;
}

.item-main-text {
	height:16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.item-sub-text {
	height:16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}

.details {
	@extend %text-small;
	color: get-color($text, 'muted');
	padding-left: 10px;
}
</style>
